import React, { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import GlobalDialog from "../../../../components/mui/Dialogue/GlobalDialog";
import * as Yup from "yup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  getLeaveGrantRequests,
  reportUpload,
  updateLeaveGrantReq,
} from "../../../../redux/features/leaveSlice";

const EditLGR = ({ row, dispatchData, open, setOpen }) => {
  // const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading } = useSelector((state) => state.leave);

  const validationSchema = Yup.object().shape({});

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues: {
      fromDate: row?.from_date ? dayjs(row?.from_date) : null,
      toDate: row?.to_date ? dayjs(row?.to_date) : null,
      reason: row?.reason || "",
      leaveTypeId: 5,
      scrnShot: row?.scrnShot || "",
      reasonType: row?.reason === "medical" ? "medical" : "other",
    },

    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        leaveID: row?.leave_id,
        reason: values.reasonType === "medical" ? "medical" : values.reason,
        leaveTypeId: values.leaveTypeId,
        fromDate: values.fromDate
          ? dayjs(values.fromDate).format("YYYY-MM-DD")
          : null,
        toDate: values.toDate
          ? dayjs(values.toDate).format("YYYY-MM-DD")
          : null,
        scrnShot: values.scrnShot,
      };
      dispatch(updateLeaveGrantReq(payload)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, {
            variant: "success",
          });
          dispatch(getLeaveGrantRequests(dispatchData));
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload.message, {
            variant: "error",
          });
        }
      });
    },
  });

  const uploadFile = async (file, dispatch) => {
    try {
      const formData = new FormData();
      formData.append("incomeScreenshot", file);
      const response = await dispatch(reportUpload(formData));
      return response.payload?.data?.filename || "";
    } catch (error) {
      console.error("File upload failed:", error);
      return "";
    }
  };

  return (
    <GlobalDialog
      title={"Edit Leave Grant Request"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"sm"}
      closeButton={true}
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box>
                <Stack direction={"column"} spacing={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From Date"
                      format="DD-MM-YYYY"
                      value={formik.values.fromDate}
                      onChange={(value) =>
                        formik.setFieldValue("fromDate", value)
                      }
                      slotProps={{
                        textField: { fullWidth: true },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="To Date"
                      format="DD-MM-YYYY"
                      value={formik.values.toDate}
                      onChange={(value) =>
                        formik.setFieldValue("toDate", value)
                      }
                      slotProps={{
                        textField: { fullWidth: true },
                      }}
                    />
                  </LocalizationProvider>
                  <FormControl
                    fullWidth
                    error={
                      formik.touched.reasonType &&
                      Boolean(formik.errors.reasonType)
                    }
                  >
                    <InputLabel id="reasonType-label">Reason</InputLabel>
                    <Select
                      labelId="reasonType-label"
                      id="reasonType"
                      value={formik.values.reasonType}
                      onChange={(e) =>
                        formik.setFieldValue("reasonType", e.target.value)
                      }
                      input={<OutlinedInput label="Reason" />}
                    >
                      <MenuItem value="medical">Medical</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  {formik.values.reasonType === "medical" && (
                    <FormControl fullWidth>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Upload Medical Report
                      </Typography>
                      <Button variant="outlined" component="label">
                        {formik.values.scrnShot ? "Change File" : "Upload File"}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const uploadedFile = await uploadFile(
                                file,
                                dispatch
                              );
                              formik.setFieldValue("scrnShot", uploadedFile);

                              e.target.value = null;
                            }
                          }}
                        />
                      </Button>

                      {formik.values.scrnShot && (
                        <Typography
                          variant="caption"
                          sx={{ mt: 1, display: "block" }}
                        >
                          {formik.values.scrnShot.replace(
                            /^incomeScreenshot[-_]*/i,
                            ""
                          )}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                  {formik.values.reasonType === "other" && (
                    <TextField
                      fullWidth
                      label="Reason"
                      name="reason"
                      value={formik.values.reason || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  )}
                  <TextField
                    fullWidth
                    label="No Of Days"
                    variant="filled"
                    value={
                      formik.values.fromDate && formik.values.toDate
                        ? dayjs(formik.values.toDate).diff(
                            dayjs(formik.values.fromDate),
                            "day"
                          ) + 1
                        : ""
                    }
                    disabled
                  />
                </Stack>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}
                >
                  <Stack direction={"row"} spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={loading}
                      variant="contained"
                      color="secondary"
                      size="small"
                      type="submit"
                    >
                      {loading ? "Saving..." : "Update"}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default EditLGR;
